/**
 * Import av leadslistor från säljpartner (Comdeva): tolkning av Excel-raderna,
 * namnmatchning mot CRM-kunderna och plan per rad. Ren logik – används av
 * importdialogen och av scripts/leadslista-preview.mjs (därför .js-ändelser
 * i importerna).
 *
 * Plan per rad:
 *   ny_kund       a) ny kund + nytt Flatbox-lead i Identifierad
 *   nytt_lead     b) befintlig kund utan Flatbox-lead → nytt Flatbox-lead
 *   oforandrad    c) befintlig kund med Flatbox-lead → leadet rörs inte; bara
 *                    tomma segment/organisationstyp på kunden fylls i
 *   bekrafta      osäker namnträff – bekräftas eller avvisas före import
 *   hoppa_over    användaren valde bort raden
 */
import { tolkaSegment, tolkaOrganisationstyp, SEGMENT_OPTIONS, ORGANISATIONSTYP_OPTIONS } from './kundformular.js';

// Kolumner som läses (rubriker jämförs utan versaler). Okända ignoreras.
const KOLUMNER = {
  organisation: 'organisation', segment: 'segment', organisationstyp: 'organisationstyp',
  kalla: 'källa', kommentar: 'kommentar', kontaktperson: 'kontaktperson', roll: 'roll', status: 'status',
};

const cell = v => String(v ?? '').replace(/\s+/g, ' ').trim();

/**
 * Raderna i en flik (array av arrayer, t.ex. XLSX sheet_to_json header:1) →
 * { rubrikrad, rader, overhoppade }. Rubrikraden är första raden med en cell
 * "Organisation". null om fliken saknar en sådan rad.
 */
export function tolkaFlik(rows) {
  const idx = (rows || []).findIndex(r => (r || []).some(c => cell(c).toLowerCase() === KOLUMNER.organisation));
  if (idx < 0) return null;
  const rubriker = rows[idx].map(c => cell(c).toLowerCase());
  const kol = Object.fromEntries(Object.entries(KOLUMNER).map(([k, h]) => [k, rubriker.indexOf(h)]));
  const rader = [];
  const overhoppade = [];
  rows.slice(idx + 1).forEach((r, i) => {
    const get = k => (kol[k] >= 0 ? cell((r || [])[kol[k]]) : '');
    const rad = {
      radnr: idx + i + 2, // Excel-radnummer (1-baserat)
      organisation: get('organisation'),
      segmentRaw: get('segment'),
      organisationstypRaw: get('organisationstyp'),
      kalla: get('kalla'),
      kommentar: get('kommentar'),
      kontaktperson: get('kontaktperson'),
      roll: get('roll'),
      status: get('status'),
    };
    if (!rad.organisation) return;
    if (/\(segment\)\s*$/i.test(rad.organisation)) { overhoppade.push({ ...rad, orsak: 'segmentrad' }); return; }
    rad.segment = tolkaSegment(rad.segmentRaw);
    rad.organisationstyp = tolkaOrganisationstyp(rad.organisationstypRaw);
    // Värden som inte finns i listorna: visas som "okänt värde: …" i
    // förhandsgranskningen och väljs om där. Sparas aldrig som fritext.
    rad.okant = {
      segment: rad.segmentRaw && !rad.segment ? rad.segmentRaw : '',
      organisationstyp: rad.organisationstypRaw && !rad.organisationstyp ? rad.organisationstypRaw : '',
    };
    rader.push(rad);
  });
  return { rubrikrad: idx + 1, rader, overhoppade };
}

/** Fliken med kolumnen "Organisation" i en arbetsbok: { [flik]: rows } → { flik, ...tolkaFlik }. */
export function tolkaArbetsbok(flikar) {
  for (const [flik, rows] of Object.entries(flikar || {})) {
    const t = tolkaFlik(rows);
    if (t) return { flik, ...t };
  }
  return null;
}

// ── Namnmatchning ──
const BORT = new Set(['ab', 'aktiebolag', 'publ', 'sverige', 'sweden', 'i']);

/**
 * "Göteborgs Stad" → "göteborgs kommun", "Uniper Sverige AB" → "uniper".
 * Gemener, utan skiljetecken, "AB" och "Sverige"; "stad" och "kommun" lika.
 * Text inom parentes tas bort (den används som alias, se namnvarianter).
 */
export function normaliseraOrgNamn(namn) {
  return String(namn || '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .normalize('NFD').replace(/(?![̈̊])[̀-ͯ]/g, '').normalize('NFC') // é → e, men å/ä/ö kvar
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(t => t && !BORT.has(t))
    .map(t => (t === 'stad' ? 'kommun' : t))
    .join(' ');
}

/** Normaliserat namn plus förkortningen inom parentes ("Svensk Kärnbränslehantering (SKB)" → skb). */
export function namnvarianter(namn) {
  const v = new Set([normaliseraOrgNamn(namn)]);
  for (const m of String(namn || '').matchAll(/\(([^)]+)\)/g)) v.add(normaliseraOrgNamn(m[1]));
  v.delete('');
  return [...v];
}

function bigram(s) {
  const t = ` ${s} `;
  const out = new Map();
  for (let i = 0; i < t.length - 1; i++) { const b = t.slice(i, i + 2); out.set(b, (out.get(b) || 0) + 1); }
  return out;
}
function dice(a, b) {
  if (!a || !b) return 0;
  const A = bigram(a), B = bigram(b);
  let gem = 0, na = 0, nb = 0;
  for (const [k, n] of A) { na += n; gem += Math.min(n, B.get(k) || 0); }
  for (const n of B.values()) nb += n;
  return (2 * gem) / (na + nb);
}

// Ord som många olika organisationer delar. Ett gemensamt sådant ord räcker
// inte för en träff ("linköpings kommun" ↔ "jönköpings kommun").
const GENERISKA_ORD = new Set([
  'och', 'kommun', 'region', 'regionen', 'hamn', 'vatten', 'avfall', 'avlopp', 'energi', 'kraft',
  'bygg', 'fastigheter', 'fastighet', 'förbundet', 'förvaltning', 'verket', 'port', 'group', 'gruppen',
]);
const NASTAN_SAMMA = 0.85; // stavningsvarianter utan gemensamt ord ("kynnigsrud" ↔ "kynningsrud")

/**
 * Likhet 0–1 mellan två normaliserade namn. 1 = samma. Ett namn vars alla ord
 * finns i det andra ("vattenfall" ↔ "vattenfall eldistribution") ger minst 0,7.
 * Utan gemensamt särskiljande ord krävs nästan samma stavning.
 */
export function namnLikhet(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const ta = a.split(' '), tb = b.split(' ');
  const d = dice(a, b);
  const sarskiljande = ta.some(t => !GENERISKA_ORD.has(t) && tb.includes(t));
  if (!sarskiljande) return d >= NASTAN_SAMMA ? d * 0.9 : 0;
  const [kort, lang] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  const delmangd = kort.every(t => lang.includes(t)) ? 0.7 + 0.25 * (kort.length / lang.length) : 0;
  return Math.max(delmangd, d * 0.9);
}

export const SAKER = 1;       // exakt normaliserad träff
export const OSAKER_MIN = 0.6; // lägre likhet visas inte som förslag

/**
 * Bästa kundträffen för en organisation.
 * → { kund, sakerhet (0–1), saker: bool, kandidater: [{ kund, sakerhet }] (topp 3) }
 */
export function matchaOrganisation(namn, kunder) {
  const varianter = namnvarianter(namn);
  const traffar = [];
  for (const k of kunder || []) {
    const kv = namnvarianter(k.name);
    let best = 0;
    for (const a of varianter) for (const b of kv) best = Math.max(best, namnLikhet(a, b));
    if (best >= OSAKER_MIN) traffar.push({ kund: k, sakerhet: best });
  }
  traffar.sort((x, y) => y.sakerhet - x.sakerhet || String(x.kund.name).localeCompare(String(y.kund.name), 'sv'));
  const exakta = traffar.filter(t => t.sakerhet >= SAKER);
  // Flera exakta träffar (dubblettkunder) räknas som osäkra – användaren väljer.
  const saker = exakta.length === 1;
  return { kund: traffar[0]?.kund || null, sakerhet: traffar[0]?.sakerhet || 0, saker, kandidater: traffar.slice(0, 3) };
}

/**
 * Plan för importen.
 * @param rader       från tolkaFlik
 * @param kunder      crmCompanies [{ id, name, segment, organisationstyp, … }]
 * @param leads       crmLeads [{ id, companyId, productId, status, … }]
 * @param flatboxProduktIds  Set med produkt-id:n som är Flatbox
 * @param val         användarens val i förhandsgranskningen:
 *                    { [radnr]: { segment, organisationstyp, kundId, hoppaOver } }
 *                    kundId: vald kund-id för en osäker träff, eller null = ny kund
 */
export function planeraImport(rader, kunder, leads, flatboxProduktIds, val = {}) {
  const flatboxLeadsPerKund = {};
  for (const l of leads || []) {
    if (l.companyId && flatboxProduktIds.has(l.productId)) (flatboxLeadsPerKund[l.companyId] ||= []).push(l);
  }
  return rader.map(rad => {
    const v = val[rad.radnr] || {};
    const m = matchaOrganisation(rad.organisation, kunder);
    // Användarens beslut går före matchningen: vald kund eller uttryckligen ny kund
    const beslutad = v.kundId !== undefined;
    const kund = beslutad ? (kunder.find(k => k.id === v.kundId) || null) : m.kund;
    const saker = beslutad || m.saker;
    const flatboxLeads = kund ? flatboxLeadsPerKund[kund.id] || [] : [];
    let atgard;
    if (v.hoppaOver) atgard = 'hoppa_over';
    else if (!kund) atgard = 'ny_kund';
    else if (!saker) atgard = 'bekrafta';
    else atgard = flatboxLeads.length ? 'oforandrad' : 'nytt_lead';
    // Kategorierna som sparas: valet i dropdownen, annars det tolkade värdet –
    // alltid ur listan. På befintlig kund fylls bara tomma fält i.
    const kategorier = {
      segment: valtVarde(rad, 'segment', val[rad.radnr]?.segment),
      organisationstyp: valtVarde(rad, 'organisationstyp', val[rad.radnr]?.organisationstyp),
    };
    const fyllI = {};
    if (kund && saker && !v.hoppaOver) {
      if (!kund.segment && kategorier.segment) fyllI.segment = kategorier.segment;
      if (!kund.organisationstyp && kategorier.organisationstyp) fyllI.organisationstyp = kategorier.organisationstyp;
    }
    return { rad, match: m, kund, flatboxLeads, atgard, kategorier, fyllI, okanda: okandaVarden(rad) };
  });
}

/** "okänt värde: …" per fält för förhandsgranskningen, t.ex. { segment: 'okänt värde: Sjukhus' }. */
export function okandaVarden(rad) {
  const out = {};
  for (const f of ['segment', 'organisationstyp']) if (rad?.okant?.[f]) out[f] = `okänt värde: ${rad.okant[f]}`;
  return out;
}

/**
 * Värdet som sparas för ett fält: användarens val i dropdownen om ett sådant
 * finns, annars det tolkade värdet. Bara värden ur listan – aldrig fritext.
 */
export function valtVarde(rad, falt, val) {
  const lista = falt === 'segment' ? SEGMENT_OPTIONS : ORGANISATIONSTYP_OPTIONS;
  const v = val !== undefined ? val : rad[falt];
  return lista.includes(v) ? v : '';
}

/** Rader med okända värden som användaren ännu inte valt om (val: { [radnr]: { segment, organisationstyp } }). */
export function olostaOkanda(rader, val = {}) {
  return rader.filter(r => ['segment', 'organisationstyp'].some(f => r.okant?.[f] && val[r.radnr]?.[f] === undefined));
}

/** Saknar kunden segment (ur listan) eller organisationstyp? */
export function saknarKategorier(kund) {
  return {
    segment: !SEGMENT_OPTIONS.includes(kund?.segment),
    organisationstyp: !ORGANISATIONSTYP_OPTIONS.includes(kund?.organisationstyp),
  };
}
